import React, { createContext, useState, useContext } from 'react';
import {ConfirmModal} from '../components/ui/modals/ConfirmModal';

const UnsavedChangesContext = createContext();

/**
 * 
 * Context used to alert users that they have unsaved data
 *
 * @returns {boolean} hasUnsavedChanges
 * @returns {Function} setHasUnsavedChanges - can be used throughout the app if changes have been made
 * @returns {Function} requestConfirm - can be used with hasUnsavedChanges to open a modal and ask the user if they'd like to continue without saving
 * @returns {boolean} showRequestConfirm - determines whether modal should be shown
 * @returns {Function} setShowRequestConfirm
 * @returns {string} modalMessage - message for modal to show, passed through requestConfirm
 * @returns {Promise<boolean>} resolve - the value of this determines when action is cancelled or not
 * 
 */
export const UnsavedChangesProvider = ({ children }) => {
    
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); 
    const [showRequestConfirm, setShowRequestConfirm] = useState(false);

    const [modalMessage, setModalMessage] = useState(null);
    const [confirmButtonText, setConfirmButtonText] = useState("Yes")
    const [cancelButtonText, setCancelButtonText] = useState("No")

    const [resolve, setResolve] = useState(null);

    const requestConfirm = (message, confirmButtonText = "Yes", cancelButtonText = "No") => {
        //show modal
        setShowRequestConfirm(true);
        
        //update modal text
        setModalMessage(message);
        setConfirmButtonText(confirmButtonText)
        setCancelButtonText(cancelButtonText)

        return new Promise((resolve) => {
            setResolve(() => resolve);
        });
    };

    const handleConfirm = () => {
        resolve(true); 
        setShowRequestConfirm(false);
        setHasUnsavedChanges(false);
    };

    const handleCancel = () => {
        resolve(false);
        setShowRequestConfirm(false);
    };

    const contextValue = {
        hasUnsavedChanges, 
        setHasUnsavedChanges, 
        requestConfirm, 
        showRequestConfirm, 
        setShowRequestConfirm, 
        modalMessage, 
        resolve
    }

    return (
        <UnsavedChangesContext.Provider value={contextValue}>
            {children}

            <ConfirmModal
                showModal={showRequestConfirm}
                message={modalMessage}
                handleConfirm={handleConfirm}
                handleCancel={handleCancel}
                confirmButtonText={confirmButtonText}
                cancelButtonText={cancelButtonText}
            />
        </UnsavedChangesContext.Provider>

        
    );
};

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);